import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { FaPlay, FaPause, FaTwitter, FaInstagram } from 'react-icons/fa';
import './GlobeMap.css';

mapboxgl.accessToken = 'pk.eyJ1IjoicHJvZml0c29scyIsImEiOiJjbTUxOGdqY3Uwcm11MmxxMml5NGM0ZDRwIn0.IxNvphcanC8re8ECFFQ2ZA';

const PLATFORM_COLORS = {
  twitter: '#1DA1F2',
  instagram: '#E1306C'
};

function GlobeMap({ isPostDeckCollapsed, selectedLocation, fetchedData }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [isRotating, setIsRotating] = useState(
    JSON.parse(localStorage.getItem("isRotating")) || true
  );
  
  useEffect(() => {
    localStorage.setItem("isRotating", JSON.stringify(isRotating));
  }, [isRotating]);
  
  const [mapLoaded, setMapLoaded] = useState(false);
  const markersRef = useRef([]);
  const circleRef = useRef(null);
  const [circleRadius, setCircleRadius] = useState(500);

  const createMarkerElement = () => {
    const el = document.createElement('div');
    el.className = 'location-marker'; // Keep the base class name

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '24');
    svg.setAttribute('height', '24');
    svg.setAttribute('fill', 'currentColor');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    // Location pin path (SVG data for location pin)
    path.setAttribute('d', 'M12 2C8.686 2 6 4.686 6 7c0 4 6 13 6 13s6-9 6-13c0-2.314-2.686-5-6-5zm0 9c-1.104 0-2-.896-2-2s.896-2 2-2 2 .896 2 2-.896 2-2 2z');
    
    svg.appendChild(path);
    el.appendChild(svg);

    return el;
};


const addMarkers = () => {
  if (!map.current) {
    console.log('Map not initialized.');
    return;
  }

  // Remove all existing markers before adding new ones
  markersRef.current.forEach(marker => marker.remove());
  markersRef.current = [];

  // Remove all existing circles (if they exist)
  map.current.getStyle().layers.forEach(layer => {
    if (layer.id.startsWith("circle-fill-")) {
      map.current.removeLayer(layer.id);
    }
  });

  map.current.getStyle().sources && Object.keys(map.current.getStyle().sources).forEach(source => {
    if (source.startsWith("circle-")) {
      map.current.removeSource(source);
    }
  });

  // If there is no fetched data, return after clearing markers
  if (!fetchedData || !Array.isArray(fetchedData) || fetchedData.length === 0) {
    console.log('No fetched data available. Clearing all markers.');
    return;
  }

  console.log(fetchedData); // Log the fetched data to verify the response

  // Loop through each post in fetchedData
  fetchedData.forEach(post => {
    // Check if lat and long are available for the post
    if (post.lat && post.long) {
      console.log(`Creating marker for: ${post.owner_username}, Lat: ${post.lat}, Long: ${post.long}`);

      // Create a marker element using the location pin
      const el = createMarkerElement();

      // Create and add the marker to the map
      const marker = new mapboxgl.Marker(el)
        .setLngLat([post.long, post.lat]) // Set the marker at the correct location
        .setPopup(new mapboxgl.Popup({ offset: 25 })
          .setHTML(`
            
          `)) // Set the image from the provided media link
        .addTo(map.current); // Add the marker to the map

      // Store the marker reference in markersRef
      markersRef.current.push(marker);

      // Add a circle around the marker location
      const circleData = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [post.long, post.lat]
        },
        properties: {
          radius: 5000 // Circle radius in meters (5 kilometers for testing)
        }
      };

      if (!map.current.getSource(`circle-${post.owner_username}`)) {
        map.current.addSource(`circle-${post.owner_username}`, {
          type: 'geojson',
          data: circleData
        });

        map.current.addLayer({
          id: `circle-fill-${post.owner_username}`,
          type: 'circle',
          source: `circle-${post.owner_username}`,
          paint: {
            'circle-radius': {
              stops: [
                [0, 0],
                [20, circleData.properties.radius / 100]
              ],
              base: 2
            },
            'circle-color': PLATFORM_COLORS.instagram, // Set the color for Instagram (you can adjust as needed)
            'circle-opacity': 0.2,
            'circle-stroke-width': 2,
            'circle-stroke-color': PLATFORM_COLORS.instagram // Use Instagram color here or customize further
          }
        });
      }
    } else {
      console.log(`Invalid location for post by: ${post.owner_username}`);
    }
  });
};


  
  
  
  // Trigger addMarkers when fetchedData is updated
  useEffect(() => {
    if (map.current) {
      if (fetchedData.length === 0) {
        // Remove all markers when fetchedData is empty
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
      } else {
        addMarkers();
      }
    }
  }, [fetchedData]);
  
  
  const updateCircle = () => {
    if (!map.current || !mapLoaded) return;
  
    const center = map.current.getCenter();
    const radiusInMeters = circleRadius * 1000; // Convert km to meters
  
    const circleData = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [center.lng, center.lat]
      },
      properties: {
        radius: radiusInMeters
      }
    };
  
    if (!map.current.getSource('circle')) {
      map.current.addSource('circle', {
        type: 'geojson',
        data: circleData
      });
    } else {
      map.current.getSource('circle').setData(circleData);
    }
  
    if (!map.current.getLayer('circle-fill')) {
      map.current.addLayer({
        id: 'circle-fill',
        type: 'circle',
        source: 'circle',
        paint: {
          'circle-radius': {
            stops: [
              [0, 0],
              [20, radiusInMeters / 100]
            ],
            base: 2
          },
          'circle-color': '#65ccb7',
          'circle-opacity': 0.2,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#65ccb7'
        }
      });
    }
  
    circleRef.current = true;
  };
  
  useEffect(() => {
    if (map.current) return; // Prevent re-initializing the map
  
    // Initialize the map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [0, 0],
      zoom: 1.5,
      projection: 'globe',
      renderWorldCopies: false,
    });
  
    map.current.addControl(new mapboxgl.NavigationControl());
  
    // Add a listener to ensure map is loaded before adding markers
    map.current.on('load', () => {
      setMapLoaded(true);
      map.current.setFog({
        'color': 'rgb(101, 204, 183)',
        'high-color': 'rgb(101, 204, 183)',
        'horizon-blend': 0.01,
        'space-color': 'rgb(11, 11, 25)',
        'star-intensity': 0.6,
      });
  
      // Now that the map is loaded, add markers
      addMarkers();
      updateCircle();
    });
  
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);
  useEffect(() => {
    if (map.current) {
      if (selectedLocation) {
        setIsRotating(false);
        const targetZoom = selectedLocation.zoom || 20;
        const duration = 3000;
  
        map.current.flyTo({
          center: selectedLocation.coordinates,
          zoom: targetZoom,
          duration: duration,
          essential: true,
        });
      } else {
        // Reset map to default position
        map.current.flyTo({
          center: [0, 0],
          zoom: 2,
          duration: 2000,
        });
  
        // Remove all markers when resetting
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];
  
        setIsRotating(true);
      }
    }
  }, [selectedLocation]);
  
  
  
  
  useEffect(() => {
    if (!map.current || !mapLoaded) return;
  
    let rotationInterval;
    if (isRotating) {
      rotationInterval = setInterval(() => {
        if (map.current) {
          map.current.easeTo({
            center: [map.current.getCenter().lng + 0.5, map.current.getCenter().lat],
            duration: 100,
            easing: (t) => t,
          });
        }
      }, 100);
    }
  
    return () => {
      if (rotationInterval) {
        clearInterval(rotationInterval);
      }
    };
  }, [isRotating, mapLoaded]);
  
  useEffect(() => {
    if (map.current) {
      setTimeout(() => {
        map.current.resize();
        updateCircle();
      }, 300);
    }
  }, [isPostDeckCollapsed]);
  

  return (
    <div className={`location-globe-container ${isPostDeckCollapsed ? 'expanded' : ''}`}>
      {/* <div className="location-radius-slider">
        <label>Circle Radius (km)</label>
        <input
          type="range"
          min="100"
          max="2000"
          value={circleRadius}
          onChange={(e) => {
            setCircleRadius(Number(e.target.value));
            updateCircle();
          }}
        />
        <span>{circleRadius} km</span>
      </div> */}
      <div ref={mapContainer} className="location-map-container" />
      {mapLoaded && (
        <button 
          className="location-rotation-toggle"
          onClick={() => setIsRotating(!isRotating)}
          title={isRotating ? "Pause rotation" : "Start rotation"}
        >
          {isRotating ? <FaPause /> : <FaPlay />}
        </button>
      )}
    </div>
  );
}

export default GlobeMap;